import { useState, useEffect } from 'react'
import { Cloud, Wind, Droplets, MapPin, RefreshCw } from 'lucide-react'

interface WeatherData {
  temperature: number
  weatherCode: number
  windSpeed: number
  humidity: number
  city: string
  country: string
}

// 天气代码映射到图标和描述
const getWeatherInfo = (code: number) => {
  const weatherMap: { [key: number]: { icon: string; desc: string; color: string } } = {
    0: { icon: '☀️', desc: '晴朗', color: 'text-yellow-400' },
    1: { icon: '🌤️', desc: '多云', color: 'text-yellow-300' },
    2: { icon: '⛅', desc: '阴天', color: 'text-gray-300' },
    3: { icon: '☁️', desc: '多云', color: 'text-gray-400' },
    45: { icon: '🌫️', desc: '雾', color: 'text-gray-400' },
    48: { icon: '🌫️', desc: '雾凇', color: 'text-gray-400' },
    51: { icon: '🌧️', desc: '小雨', color: 'text-blue-300' },
    53: { icon: '🌧️', desc: '中雨', color: 'text-blue-400' },
    55: { icon: '🌧️', desc: '大雨', color: 'text-blue-500' },
    61: { icon: '🌧️', desc: '小雨', color: 'text-blue-300' },
    63: { icon: '🌧️', desc: '中雨', color: 'text-blue-400' },
    65: { icon: '🌧️', desc: '大雨', color: 'text-blue-500' },
    71: { icon: '❄️', desc: '小雪', color: 'text-white' },
    73: { icon: '❄️', desc: '中雪', color: 'text-white' },
    75: { icon: '❄️', desc: '大雪', color: 'text-white' },
    95: { icon: '⛈️', desc: '雷雨', color: 'text-purple-400' },
  }
  return weatherMap[code] || { icon: '☁️', desc: '多云', color: 'text-gray-400' }
}

// 主要城市列表
const MAJOR_CITIES = [
  { name: '北京', lat: 39.9042, lon: 116.4074, country: '中国' },
  { name: '上海', lat: 31.2304, lon: 121.4737, country: '中国' },
  { name: '广州', lat: 23.1291, lon: 113.2644, country: '中国' },
  { name: '深圳', lat: 22.5431, lon: 114.0579, country: '中国' },
  { name: '成都', lat: 30.5728, lon: 104.0668, country: '中国' },
  { name: '杭州', lat: 30.2741, lon: 120.1551, country: '中国' },
  { name: '武汉', lat: 30.5928, lon: 114.3055, country: '中国' },
  { name: '西安', lat: 34.3416, lon: 108.9398, country: '中国' },
  { name: '纽约', lat: 40.7128, lon: -74.0060, country: '美国' },
  { name: '伦敦', lat: 51.5074, lon: -0.1278, country: '英国' },
  { name: '东京', lat: 35.6762, lon: 139.6503, country: '日本' },
  { name: '巴黎', lat: 48.8566, lon: 2.3522, country: '法国' },
  { name: '悉尼', lat: -33.8688, lon: 151.2093, country: '澳大利亚' },
  { name: '新加坡', lat: 1.3521, lon: 103.8198, country: '新加坡' },
  { name: '迪拜', lat: 25.2048, lon: 55.2708, country: '阿联酋' },
]

export default function WeatherWidget() {
  const [weather, setWeather] = useState<WeatherData | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [currentCityIndex, setCurrentCityIndex] = useState(0)

  const fetchWeather = async (cityIndex: number) => {
    try {
      setLoading(true)
      setError(null)
      
      const city = MAJOR_CITIES[cityIndex]
      
      // 使用 Open-Meteo API（免费，无需API key）
      const response = await fetch(
        `https://api.open-meteo.com/v1/forecast?latitude=${city.lat}&longitude=${city.lon}&current=temperature_2m,relative_humidity_2m,weather_code,wind_speed_10m`
      )
      
      if (!response.ok) {
        throw new Error('获取天气失败')
      }
      
      const data = await response.json()
      
      setWeather({
        temperature: Math.round(data.current.temperature_2m),
        weatherCode: data.current.weather_code,
        windSpeed: data.current.wind_speed_10m,
        humidity: data.current.relative_humidity_2m,
        city: city.name,
        country: city.country,
      })
    } catch (err) {
      setError('获取天气信息失败')
      console.error('Weather fetch error:', err)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchWeather(currentCityIndex)
  }, [currentCityIndex])

  const handleRefresh = () => {
    fetchWeather(currentCityIndex)
  }

  const handleNextCity = () => {
    setCurrentCityIndex((prev) => (prev + 1) % MAJOR_CITIES.length)
  }

  const weatherInfo = weather ? getWeatherInfo(weather.weatherCode) : null

  return (
    <div className="bg-gradient-to-br from-blue-500/20 to-cyan-500/20 rounded-xl p-4 border border-blue-500/30">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-lg font-semibold text-telegram-text flex items-center">
          <Cloud className="w-5 h-5 mr-2 text-blue-400" />
          实时天气
        </h3>
        <div className="flex items-center space-x-2">
          <button
            onClick={handleRefresh}
            className="p-1.5 rounded-lg bg-telegram-dark hover:bg-telegram-dark/80 transition-colors"
            disabled={loading}
          >
            <RefreshCw className={`w-4 h-4 text-telegram-secondary ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {loading && !weather ? (
        <div className="flex items-center justify-center py-6">
          <div className="w-6 h-6 border-2 border-blue-400 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : error ? (
        <div className="text-center py-4">
          <p className="text-telegram-secondary text-sm">{error}</p>
          <button
            onClick={handleRefresh}
            className="mt-2 px-4 py-1.5 bg-blue-500/20 text-blue-400 rounded-lg text-sm"
          >
            重试
          </button>
        </div>
      ) : weather && weatherInfo ? (
        <div className="space-y-4">
          {/* 主要天气信息 */}
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <span className="text-5xl">{weatherInfo.icon}</span>
              <div>
                <div className={`text-3xl font-bold ${weatherInfo.color}`}>
                  {weather.temperature}°C
                </div>
                <div className="text-telegram-secondary text-sm">
                  {weatherInfo.desc}
                </div>
              </div>
            </div>
            <button
              onClick={handleNextCity}
              className="flex items-center space-x-1 px-3 py-1.5 bg-telegram-dark rounded-lg text-sm text-telegram-text hover:bg-telegram-dark/80 transition-colors"
            >
              <MapPin className="w-4 h-4 text-telegram-secondary" />
              <span>{weather.city}</span>
            </button>
          </div>

          {/* 详细数据 */}
          <div className="grid grid-cols-3 gap-2 pt-3 border-t border-telegram-secondary/20">
            <div className="text-center">
              <Wind className="w-4 h-4 text-telegram-secondary mx-auto mb-1" />
              <p className="text-xs text-telegram-secondary">风速</p>
              <p className="text-sm font-medium text-telegram-text">{weather.windSpeed} km/h</p>
            </div>
            <div className="text-center">
              <Droplets className="w-4 h-4 text-telegram-secondary mx-auto mb-1" />
              <p className="text-xs text-telegram-secondary">湿度</p>
              <p className="text-sm font-medium text-telegram-text">{weather.humidity}%</p>
            </div>
            <div className="text-center">
              <Cloud className="w-4 h-4 text-telegram-secondary mx-auto mb-1" />
              <p className="text-xs text-telegram-secondary">天气</p>
              <p className="text-sm font-medium text-telegram-text">{weatherInfo.desc}</p>
            </div>
          </div>

          {/* 城市切换提示 */}
          <p className="text-xs text-telegram-secondary/70 text-center">
            点击城市名称切换不同地区
          </p>
        </div>
      ) : null}
    </div>
  )
}
